import React, { useEffect, useState, useMemo } from 'react'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import {
  Role,
  UserAccountDetails,
  UserRoleAssignment as UserRoleAssignmentSchema,
  Region,
  PaginationResponse,
} from '../../../swagger'
import { dateToDashString, IS_UTC } from '../../../utils/dateUtility'
import { TextButtonVariant } from '../../Buttons/TextButton'
import Header, { HeaderVariant } from '../../Elements/Header'
import RoleModal, { EditRoleArgs, AssignRoleArgs } from '../../Modals/RoleModal'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardFormHeader from '../../Card/CardFormHeader'
import ContainedButton, {
  ContainedButtonVariant,
} from '../../Buttons/ContainedButton'
import { useShowOnDesktop } from '../../../hooks/useShowOnDesktop'
import { regionsApi, userAccountsApi } from '../../../api/swagger'
import ConfirmationModal from '../../Modals/ConfirmationModal'
import ActionButtons from '../../Buttons/ActionButtons'
import { Can } from '@casl/react'
import { useAuth } from '../../Routes/AuthProvider'
import { useNavigate } from 'react-router'
import { styled } from '@mui/system'
import { useLoadingIds } from '../../../hooks/useLoadingIds'
import useLoadingContext from '../../../hooks/useLoadingContext'
import { useMountEffect } from '../../../hooks/useMountEffect'
import {
  ActionableTable,
  ActionableTableColumn,
} from '../../Table/ActionableTable'
import { Action } from '../../Table/RowActions'
import { GridValueFormatterParams } from '@mui/x-data-grid'
import { useTheme } from '@mui/material'
import { useAppSnackbar } from '../../../hooks/useAppSnackbar'

/** Default end date for roles that don't have an end date selected (validTo) */
export const DEFAULT_END_DATE = new Date('9999-12-31T23:59:59.000Z')

const RolesCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  marginLeft: theme.spacing(0),
  [theme.breakpoints.down('sm')]: {
    // Don't let the roles table get overlaid by buttons on mobile
    marginBottom: theme.spacing(12),
  },
}))

const RolesHeader = styled(Header)(({ theme }) => ({
  marginTop: theme.spacing(3),
  marginLeft: theme.spacing(3),
}))

interface RolesTableProps {
  roles?: UserRoleAssignment[]
  roleOptions?: Role[]
  userKey: number
  userAccount: UserAccountDetails
  removeRole: () => void
  triggerRefresh?: (actorKey?: number) => void
  pagination: PaginationResponse
  handleUserRolesPagination: (pageInformation: PaginationResponse) => void
}

export interface UserRoleAssignment
  extends Omit<UserRoleAssignmentSchema, 'validTo'> {
  validTo?: Date
}

export const RolesTable: React.FC<RolesTableProps> = ({
  roles,
  roleOptions,
  userKey,
  userAccount,
  removeRole,
  triggerRefresh,
  handleUserRolesPagination,
  pagination,
}) => {
  const theme = useTheme()
  const { permissionAbility } = useAuth()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isShowOnDesktop = useShowOnDesktop()
  const errorMessage = t('UserAccount.Error', 'Something went wrong.')

  const { handleError, handleMessage } = useAppSnackbar()

  const {
    Regions: { fetchRegions: fetchRegionsLoadingId },
    AssignRole: {
      assignRole: assignRoleLoadingId,
      editRole: editRoleLoadingId,
    },
  } = useLoadingIds()

  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false)
  const [rolesMap, setRolesMap] = useState(
    new Map<number /* actorKey */, UserRoleAssignment /* role information */>()
  )
  const [selectedRole, setSelectedRole] = useState<
    UserRoleAssignment | undefined
  >()
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [userRegionsForRoleModal, setUserRegionsForRoleModal] = useState<
    Region[]
  >([])

  const rolesRows = useMemo(() => {
    return [...rolesMap.values()]
  }, [rolesMap])

  const handleEditRoleClick = (actorKey: number) => {
    setIsRoleModalOpen(true)
    setSelectedRole(rolesMap.get(actorKey))
  }

  const fetchUserRegionsForRoleModal = async () => {
    try {
      const userRegionsForRoleModal = await regionsApi.fetchRegions()

      setUserRegionsForRoleModal(userRegionsForRoleModal.regions)
    } catch (error) {
      await handleError(error, errorMessage)
    }
  }

  const {
    triggerFetch: triggerFetchRegions,
    isLoading: isLoadingFetchRegions,
    removeLoadingIds,
  } = useLoadingContext({
    asyncFunction: fetchUserRegionsForRoleModal,
    loadingId: fetchRegionsLoadingId,
  })

  useMountEffect(() => {
    if (permissionAbility.can('view', 'Region')) {
      triggerFetchRegions()
      return
    }
    removeLoadingIds([fetchRegionsLoadingId])
  })

  useEffect(() => {
    if (!!roles) {
      const map = new Map<
        number /* actorKey */,
        UserRoleAssignment /* role information */
      >()
      roles.forEach((role) => {
        map.set(role.actorKey, role)
      })
      setRolesMap(map)
    }
  }, [roles])

  const updateRoleForParent = (role: UserRoleAssignment) => {
    setRolesMap((prevMap) => {
      const newMap = new Map(prevMap)
      newMap.set(role.actorKey, role)
      return newMap
    })
    setIsRoleModalOpen((isOpen) => !isOpen)
  }

  const closeRoleModal = () => {
    setIsRoleModalOpen(false)
    setSelectedRole(undefined)
    triggerRefresh?.()
  }

  const [actorKeyToRemove, setActorKeyToRemove] = useState(-1)

  const handleRemoveRole = (actorKey: number) => {
    setActorKeyToRemove(actorKey)
    setSelectedRole(rolesMap.get(actorKey))
    setShowConfirmationModal(true)
  }

  const handleAcceptConfirmation = async (
    event: React.FormEvent<HTMLDivElement>
  ) => {
    event.preventDefault()
    const roleActorKey = userAccount.roles.filter(
      (role) => role.actorKey === actorKeyToRemove
    )
    try {
      await userAccountsApi.unassignRole({
        userKey,
        actorKey: roleActorKey[0].actorKey,
      })
      removeRole()
      triggerRefresh?.()
      setShowConfirmationModal(false)
      handleMessage(
        t(
          'RolesTable.RoleModal.ValidationMessage.Success',
          'Successfully removed role.'
        )
      )
    } catch (e) {
      await handleError(
        e,
        t(
          'RolesTable.RoleModal.ValidationMessage.AddTransaction',
          'Something went wrong while removing the role.'
        )
      )
    }
  }

  const handleEditRole = async ({
    actorKey,
    roleKey,
    supervisorActorKey,
    supervisorName,
    roleName,
    validFrom,
    validTo,
    regionKey,
    region,
  }: EditRoleArgs) => {
    try {
      await userAccountsApi.editRole({
        userKey,
        actorKey,
        body: {
          supervisorActorKey: supervisorActorKey as number, // we guarantee this is defined by disabling save button if it isn't
          validFrom,
          validTo,
          regionKey,
        },
      })

      handleMessage(
        t('Roles.EditRole.SuccessMessage', 'Role successfully updated.')
      )

      // We only update the changes made
      updateRoleForParent({
        actorKey,
        roleKey,
        region,
        regionKey,
        name: roleName,
        supervisor: supervisorName,
        validFrom,
        validTo,
      })
    } catch (e) {
      await handleError(e, errorMessage)
    } finally {
      closeRoleModal()
    }
  }

  const handleAssignRole = async ({
    roleKey,
    supervisorActorKey,
    supervisorName,
    roleName,
    validFrom,
    validTo,
    regionKey,
    region,
  }: AssignRoleArgs) => {
    try {
      const response = await userAccountsApi.assignRole({
        roleKey,
        userKey,
        body: {
          supervisorActorKey: supervisorActorKey as number, // we guarantee this is defined by disabling save button if it isn't
          validFrom,
          validTo,
          regionKey,
        },
      })

      handleMessage(
        t('Roles.AssignRole.SuccessMessage', 'Role successfully assigned.')
      )

      updateRoleForParent({
        actorKey: response.actorKey,
        roleKey,
        name: roleName,
        region,
        regionKey,
        supervisor: supervisorName,
        validFrom,
        /**
         * If validTo is undefined, set it to the default end date
         * This is to ensure that the role is displayed with the correct end date
         * since the update occurs initially in the client and then the server updates the role
         *
         * (prevents a blank space in end date column)
         **/
        validTo: validTo ?? DEFAULT_END_DATE,
      })
    } catch (e) {
      await handleError(e, errorMessage)
    } finally {
      closeRoleModal()
    }
  }

  const headerMessage = `${t(
    'RolesTable.ConfirmationModal.Header.RemoveRole',
    'You are about to remove the role {{selectedRole}} for this user.',
    { selectedRole: selectedRole?.name }
  )}`

  const confirmationBodyMessage = (
    <Typography variant="body1" component="p" align="center">
      {t(
        'ConfirmationModal.ConfirmationModal.Body',
        'This action is not reversible and by confirming "Yes" an email will be sent informing the user, team leaders, HR and Accounting that the role has been ended.'
      )}
    </Typography>
  )

  const handleConfirmationCancel = async () => {
    setShowConfirmationModal(false)
  }

  const confirmationButtons = (
    <ActionButtons
      primaryButtonLabel={ContainedButtonVariant.YesRemove}
      secondaryButtonLabel={TextButtonVariant.NoCancel}
      secondaryClick={handleConfirmationCancel}
    />
  )

  const roleRemovalConfirmationProps = {
    isOpen: showConfirmationModal,
    dialogTitle: headerMessage,
    dialogContent: confirmationBodyMessage,
    dialogActions: confirmationButtons,
    handleFormSubmit: handleAcceptConfirmation,
  }

  const primaryButtonCallback = () => {
    !!selectedRole
      ? removeLoadingIds([editRoleLoadingId])
      : removeLoadingIds([assignRoleLoadingId])
  }

  const navigateToSupervisorAccountPage = (actorKey?: number) => {
    if (actorKey) {
      navigate({
        pathname: `/admin/user-accounts/${actorKey}`,
      })
    }
  }

  const navigateToTeam = (row: UserRoleAssignment | undefined) => {
    if (row) {
      navigate(
        {
          pathname: '/team',
        },
        {
          state: {
            userKey,
            actorKey: row.actorKey,
            firstName: userAccount.firstName,
            lastName: userAccount.lastName,
          },
        }
      )
    }
  }

  const minWidthOnDesktop = isShowOnDesktop ? { minWidth: 300 } : {}

  const actionableTableColumnsHeader: ActionableTableColumn[] = [
    {
      fieldName: 'name',
      columnHeaderName: t('UserAccounts.Roles.Table.Header.Role', 'Role'),
      ...{ ...minWidthOnDesktop },
      renderCell: (params) => {
        if (new Date(params.row.validTo).getTime() < new Date().getTime()) {
          return (
            <div className="MuiDataGrid-cellContent">
              <s>{params.row.name}</s>
            </div>
          )
        }
        return <div className="MuiDataGrid-cellContent">{params.row.name}</div>
      },
      sortable: true,
      sortingOrder: ['desc', 'asc'],
    },
    {
      fieldName: 'region',
      columnHeaderName: t(
        'UserAccounts.Roles.Table.Header.ActorRegion',
        'Actor Region'
      ),
    },
    {
      fieldName: 'actorKey',
      columnHeaderName: t(
        'UserAccounts.Roles.Table.Header.ActorKey',
        'Actor Key'
      ),
      sortable: true,
      sortingOrder: ['desc', 'asc'],
    },
    {
      fieldName: 'validFrom',
      columnHeaderName: t(
        'UserAccounts.Roles.Table.Header.StartDate',
        'Start Date'
      ),
      valueFormatter: (params: GridValueFormatterParams<Date>) => {
        if (!!params.value) {
          return dateToDashString(params.value, IS_UTC)
        }
        return ''
      },
      sortable: true,
      sortingOrder: ['desc', 'asc'],
    },
    {
      fieldName: 'validTo',
      columnHeaderName: t(
        'UserAccounts.Roles.Table.Header.EndDate',
        'End Date'
      ),
      valueFormatter: (params: GridValueFormatterParams<Date>) => {
        if (!!params.value) {
          return dateToDashString(params.value, IS_UTC)
        }
        return ''
      },
      sortable: true,
      sortingOrder: ['desc', 'asc'],
    },
    {
      fieldName: 'supervisor',
      columnHeaderName: t(
        'UserAccounts.Roles.Table.Header.TeamLeader',
        'Team Leader'
      ),
      valueFormatter: (
        params: GridValueFormatterParams<string | undefined>
      ) => {
        return params.value ?? t('Admin.RolesTable.Supervisor.None', 'None')
      },
      sortable: true,
      sortingOrder: ['desc', 'asc'],
    },
  ]

  const rowActions: Action<UserRoleAssignment>[] = [
    {
      actionName: t('Admin.RolesTable.RowAction.Edit', 'Edit'),
      actionKey: 'edit',
      actionFunction: (row) => handleEditRoleClick(row?.actorKey ?? 0),
      hide: (row) => {
        const isValidRole = (row?.validTo as Date) > new Date()
        const canEditExpiredRoles = permissionAbility.can(
          'editExpiredRoles',
          'User'
        )
        /** If we are not valid in the future or we don't have the ability to edit expired roles */
        return !isValidRole && !canEditExpiredRoles
      },
    },
    {
      actionName: t('Admin.RolesTable.RowAction.Remove', 'Remove'),
      actionKey: 'remove',
      actionFunction: (row) => handleRemoveRole(row?.actorKey ?? 0),
      hide: () => !permissionAbility.can('unassignRole', 'User'),
    },
    {
      actionName: t('Admin.RolesTable.RowAction.ViewSupport', 'View Support'),
      actionKey: 'viewSupervisor',
      actionFunction: (row) =>
        navigateToSupervisorAccountPage(row?.supervisorUserKey),
      hide: () => !permissionAbility.can('viewSupervisor', 'User'),
    },
    {
      actionName: t('Admin.RolesTable.RowAction.ViewTeam', 'View Team'),
      actionKey: 'viewTeam',
      actionFunction: (row) => navigateToTeam(row),
      hide: () => !permissionAbility.can('view', 'User'),
    },
  ]

  return (
    <>
      <ConfirmationModal {...roleRemovalConfirmationProps} />
      <RoleModal
        regions={userRegionsForRoleModal}
        isOpen={isRoleModalOpen}
        onClose={closeRoleModal}
        initialRole={selectedRole}
        roleOptions={roleOptions}
        handleEditRole={handleEditRole}
        handleAssignRole={handleAssignRole}
        primaryButtonLoadingId={
          !!selectedRole ? editRoleLoadingId : assignRoleLoadingId
        }
        primaryButtonCallback={primaryButtonCallback}
      />

      <RolesCard>
        <CardFormHeader
          header={
            <RolesHeader
              id="rolesTableHeader"
              headerName={t('UserAccountCard.Header.Roles', 'Roles')}
              component="h3"
              variant={HeaderVariant.Card}
            />
          }
          buttons={
            <Can I={'accessRolesTab'} on="Admin" ability={permissionAbility}>
              <Box mr={2} mt={3} ml={isShowOnDesktop ? 0 : 2}>
                <ContainedButton
                  id="assignRoleToUserButton"
                  variant={ContainedButtonVariant.AssignRole}
                  fullWidth={!isShowOnDesktop}
                  onClick={() => setIsRoleModalOpen(true)}
                />
              </Box>
            </Can>
          }
          useDivider
        />
        <ActionableTable
          columns={actionableTableColumnsHeader}
          rows={rolesRows}
          noResultsMessage={t(
            'UserAccount.Roles.Table.NoRoles',
            'No roles assigned to this user'
          )}
          rowActions={rowActions}
          customCssTable={{
            paddingLeft: theme.spacing(0.8),
            height: theme.spacing(14),
            ...(!rolesRows.length
              ? {
                  height: theme.spacing(14),
                  '& .MuiBox-root': {
                    alignItems: 'flex-start',
                    padding: `${theme.spacing(0.6)} ${theme.spacing(1.5)}`,
                  },
                }
              : {}),
          }}
          handlePaginationChange={handleUserRolesPagination}
          pagination={{
            ...pagination,
            page: pagination.page - 1,
          }}
          isLoading={isLoadingFetchRegions}
        />
      </RolesCard>
    </>
  )
}

export default RolesTable
